import React, { useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import {
  type HeroImageV1,
  type RestSixtyFortyColumnModal,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  CloseOfferButton,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestSixtyFortyColumnModal;
  detailedAction: React.ReactElement;
  close: () => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-top: 10px;
  padding-bottom: 10px;
`;

export const SixtyFortyColumnV2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { title: titleP, details },
    },
    mobileLayout,
  },
  detailedAction,
  close,
}) => {
  const { mobileView } = useMediaQueries();

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement | HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  const offerContext = useContext(EditOfferPageContext);

  const imgUrl = (hero as HeroImageV1).url;

  const title = <TitleTheme title={titleP} />;
  const copy = <CustomerHtml value={details} />;

  const mobileViewContent = (
    <ModalMobileInnerSpacer
      className={cs('display-grid', 'sixty-forty', 'mobile', 'inner-spacer')}
    >
      <OfferTitleMobile
        ref={titleRef as React.RefObject<HTMLDivElement>}
        title={title}
        close={close}
      />
      <img
        alt=""
        src={imgUrl}
        ref={imageRef}
        css={css`
          width: 100%;
          height: 100%;
          max-height: 300px;
          object-fit: cover;
          object-position: center;
          border-radius: 4px;
        `}
      />
      <CopyContent ref={descRef}>{copy}</CopyContent>
      <div ref={actionsRef}>{detailedAction}</div>
    </ModalMobileInnerSpacer>
  );

  if (offerContext && offerContext?.previewDevice === PreviewDevice.MOBILE) {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {mobileViewContent}
      </MobileDevicePreview>
    );
  }

  if (mobileView) return mobileViewContent;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        padding: 0;
      `}
      className={cs('sixty-forty', 'display-grid', 'desktop', 'inner-spacer')}
    >
      <img
        ref={imageRef}
        alt=""
        src={imgUrl}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
      <div
        css={css`
          padding: 1rem;
        `}
        className={cs('display-grid', 'content-grid')}
      >
        <CloseOfferButton
          onClick={close}
          className={cs('justify-self--end', 'z-10')}
        />
        <CopyContent className={cs('align-self--center')}>
          <span ref={titleRef}>
            <TitleTheme title={titleP} css={{ marginBottom: '1rem' }} />
          </span>
          <div ref={descRef}>{copy}</div>
        </CopyContent>
        <div ref={actionsRef}>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};
